import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Divider,
} from '@mui/material';
import { motion } from 'framer-motion';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SecurityIcon from '@mui/icons-material/Security';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BuildIcon from '@mui/icons-material/Build';
import SchoolIcon from '@mui/icons-material/School';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import GppGoodIcon from '@mui/icons-material/GppGood';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import LockIcon from '@mui/icons-material/Lock';
import ShieldIcon from '@mui/icons-material/Shield';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import DataThresholdingIcon from '@mui/icons-material/DataThresholding';
import { Link } from 'react-router-dom';

const Iso27001: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const implementationProcess = [
    {
      title: "Security Gap Analysis",
      description: "We assess your current security controls against ISO 27001 requirements to identify vulnerabilities and improvement areas.",
      icon: <AssessmentIcon fontSize="large" />
    },
    {
      title: "ISMS Planning",
      description: "We define the scope of your Information Security Management System and establish information security objectives.",
      icon: <ViewInArIcon fontSize="large" />
    },
    {
      title: "Risk Assessment",
      description: "We conduct a comprehensive risk assessment to identify, analyze and evaluate information security risks.",
      icon: <PrivacyTipIcon fontSize="large" />
    },
    {
      title: "Control Implementation",
      description: "We help you implement appropriate security controls from Annex A based on your risk assessment results.",
      icon: <SettingsSuggestIcon fontSize="large" />
    },
    {
      title: "Documentation",
      description: "We develop the necessary policies, procedures, and records required for ISO 27001 compliance.",
      icon: <FactCheckIcon fontSize="large" />
    },
    {
      title: "Training",
      description: "We train your team on information security best practices and awareness of their responsibilities.",
      icon: <SchoolIcon fontSize="large" />
    },
    {
      title: "Internal Audit",
      description: "We conduct internal audits to verify the effectiveness of your ISMS before certification.",
      icon: <BuildIcon fontSize="large" />
    },
    {
      title: "Certification Support",
      description: "We guide you through the certification audit process and help address any non-conformities.",
      icon: <GppGoodIcon fontSize="large" />
    }
  ];

  const keyControls = [
    {
      title: "Access Control",
      description: "Comprehensive systems to ensure only authorized individuals can access sensitive information and systems.",
      icon: <LockIcon fontSize="large" />
    },
    {
      title: "Cryptography",
      description: "Implementation of encryption and key management to protect sensitive information.",
      icon: <ShieldIcon fontSize="large" />
    },
    {
      title: "Operations Security",
      description: "Secure operational procedures and responsibilities to protect against malware and technical vulnerabilities.",
      icon: <SecurityIcon fontSize="large" />
    },
    {
      title: "Communications Security",
      description: "Protection of information in networks and its supporting information processing facilities.",
      icon: <DataThresholdingIcon fontSize="large" />
    },
    {
      title: "System Acquisition & Development",
      description: "Security requirements integrated into the development lifecycle of information systems.",
      icon: <SettingsSuggestIcon fontSize="large" />
    },
    {
      title: "Incident Management",
      description: "Processes to identify, report, assess, and respond to information security incidents.",
      icon: <MonitorHeartIcon fontSize="large" />
    }
  ];

  const benefits = [
    "Protection of sensitive information from unauthorized access and breaches",
    "Compliance with legal, regulatory, and contractual requirements",
    "Systematic approach to identifying and managing information security risks",
    "Improved stakeholder confidence and trust in your organization",
    "Better resilience against cyber attacks and security threats",
    "Reduced probability and impact of security incidents",
    "Competitive advantage in tender situations that require ISO 27001",
    "Development of a security-aware organizational culture",
    "Cost savings through reduced incidents and improved efficiency",
    "Integration with other management systems like ISO 9001 or ISO 20000"
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
          color: 'white',
          py: 12,
          position: 'relative',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom textAlign="center">
              ISO 27001:2022 Information Security
            </Typography>
            <Typography variant="h6" paragraph align="center" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.9 }}>
              Comprehensive implementation and certification services to protect your critical information assets
            </Typography>
          </motion.div>
        </Container>
        
        {/* Decorative Elements */}
        <Box sx={{
          position: 'absolute',
          top: 60,
          right: 120,
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: 40,
          left: 100,
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
      </Box>

      {/* Overview Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom 
                fontWeight="bold"
                color="#0c2340"
              >
                What is ISO 27001:2022?
              </Typography>
              <Typography paragraph>
                ISO 27001:2022 is the international standard for Information Security Management Systems (ISMS). 
                It provides a framework for establishing, implementing, maintaining, and continually improving 
                an information security management system within your organization.
              </Typography>
              <Typography paragraph>
                This standard takes a risk-based approach to information security, helping organizations identify, 
                analyze, and address information security risks through the implementation of appropriate security 
                controls and measures.
              </Typography>
              <Typography paragraph>
                ISO 27001 certification demonstrates to clients, partners, and stakeholders that your organization 
                is committed to protecting their information and has implemented a comprehensive system to manage 
                information security risks effectively.
              </Typography>
            </motion.div>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Box 
                component="img"
                src="https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                alt="Information Security"
                sx={{
                  width: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                }}
              />
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* Key Security Controls Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
              sx={{ mb: 6 }}
            >
              Key Security Controls
            </Typography>
          </motion.div>
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(3, 1fr)' 
            },
            gap: 4 
          }}>
            {keyControls.map((control, index) => (
              <Box key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      height: '100%',
                      borderRadius: '16px',
                      boxShadow: '0 5px 20px rgba(0,0,0,0.05)',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-10px)',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                      }
                    }}
                  >
                    <Box sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '4px',
                      bgcolor: '#2c387e',
                    }} />
                    <Box sx={{
                      width: 60,
                      height: 60,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      backgroundColor: '#2c387e15',
                      color: '#2c387e',
                      mb: 3,
                    }}>
                      {control.icon}
                    </Box>
                    <Typography
                      variant="h5"
                      component="h3"
                      gutterBottom
                      fontWeight="bold"
                      color="#0c2340"
                    >
                      {control.title}
                    </Typography>
                    <Typography color="text.secondary">
                      {control.description}
                    </Typography>
                  </Paper>
                </motion.div>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Implementation Process Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            fontWeight="bold"
            color="#0c2340"
            textAlign="center"
            sx={{ mb: 2 }}
          >
            Our ISO 27001 Implementation Process
          </Typography>
          <Typography 
            variant="body1" 
            paragraph 
            align="center" 
            sx={{ maxWidth: '800px', mx: 'auto', mb: 6 }}
          >
            We follow a structured approach to implement ISO 27001:2022 that ensures effective protection of your information assets
          </Typography>
        </motion.div>
        
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)', 
            md: 'repeat(4, 1fr)' 
          },
          gap: 4 
        }}>
          {implementationProcess.map((step, index) => (
            <Box key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    borderRadius: '16px',
                    boxShadow: '0 5px 20px rgba(0,0,0,0.05)',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2
                  }}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      backgroundColor: '#2c387e15',
                      color: '#2c387e',
                      mr: 2
                    }}>
                      {step.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      component="h3"
                      fontWeight="bold"
                      color="#0c2340"
                    >
                      {step.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </Paper>
              </motion.div>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Risk Management Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              >
                <Typography 
                  variant="h4" 
                  component="h2" 
                  gutterBottom 
                  fontWeight="bold"
                  color="#0c2340"
                >
                  Risk-Based Approach to Information Security
                </Typography>
                <Typography paragraph>
                  ISO 27001 places a strong emphasis on risk management as the foundation of an effective 
                  information security management system. Our approach helps you implement a robust risk 
                  assessment and treatment process that:
                </Typography>
                <List>
                  {[
                    "Identifies information assets and their value to your organization",
                    "Determines potential threats and vulnerabilities to those assets",
                    "Analyzes and evaluates the level of risk associated with each potential security breach",
                    "Selects appropriate risk treatment options (mitigate, transfer, avoid, or accept)",
                    "Implements security controls from Annex A that address identified risks",
                    "Continuously monitors and reviews the effectiveness of your risk treatment plan"
                  ].map((point, index) => (
                    <ListItem key={index} disablePadding sx={{ mb: 2 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={point} />
                    </ListItem>
                  ))}
                </List>
              </motion.div>
            </Box>
            <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              >
                <Box 
                  component="img"
                  src="https://images.unsplash.com/photo-1594142404303-3d99b8aa7ebd?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                  alt="Risk Assessment"
                  sx={{
                    width: '100%',
                    borderRadius: '16px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                  }}
                />
              </motion.div>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            fontWeight="bold"
            color="#0c2340"
            textAlign="center"
            sx={{ mb: 6 }}
          >
            Benefits of ISO 27001 Certification
          </Typography>
        </motion.div>
        
        <Paper
          elevation={0}
          sx={{
            p: 5,
            borderRadius: '16px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #f9f9f9 0%, #f5f7fa 100%)'
          }}
        >
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: 4 
          }}>
            <Box>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              >
                <List>
                  {benefits.slice(0, Math.ceil(benefits.length / 2)).map((benefit, index) => (
                    <ListItem key={index} disablePadding sx={{ mb: 2 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={benefit} />
                    </ListItem>
                  ))}
                </List>
              </motion.div>
            </Box>
            <Box>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.2 }}
              >
                <List>
                  {benefits.slice(Math.ceil(benefits.length / 2)).map((benefit, index) => (
                    <ListItem key={index} disablePadding sx={{ mb: 2 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={benefit} />
                    </ListItem>
                  ))}
                </List>
              </motion.div>
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* Security Incident Management Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 6,
            alignItems: 'center' 
          }}>
            <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              >
                <Box 
                  component="img"
                  src="https://images.unsplash.com/photo-1548092372-0d1bd40894a3?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                  alt="Security Operations Center"
                  sx={{
                    width: '100%',
                    borderRadius: '16px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                  }}
                />
              </motion.div>
            </Box>
            <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              >
                <Typography 
                  variant="h4" 
                  component="h2" 
                  gutterBottom 
                  fontWeight="bold"
                  color="#0c2340"
                >
                  Security Incident Management
                </Typography>
                <Typography paragraph>
                  A key component of ISO 27001 is establishing an effective information security incident 
                  management process. We help you implement a comprehensive approach to:
                </Typography>
                <List>
                  {[
                    "Detect and report information security events and incidents promptly",
                    "Assess incidents to determine their severity and impact",
                    "Respond to incidents effectively to minimize damage",
                    "Learn from incidents to improve your security controls and procedures",
                    "Collect evidence and maintain proper documentation for incidents",
                    "Establish clear roles and responsibilities for incident management"
                  ].map((point, index) => (
                    <ListItem key={index} disablePadding sx={{ mb: 2 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={point} />
                    </ListItem>
                  ))}
                </List>
              </motion.div>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Why Choose Us Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 6,
          alignItems: 'center' 
        }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom 
                fontWeight="bold"
                color="#0c2340"
              >
                Why Choose RTT for ISO 27001?
              </Typography>
              <Typography paragraph>
                Right Track Technologies Limited offers comprehensive ISO 27001 implementation and certification 
                services that focus on practical, risk-based approaches to information security. Our 
                experienced consultants work with you to develop a system that protects your critical 
                information assets while enabling your business to operate effectively.
              </Typography>
              
              <List>
                {[
                  "Expertise: Our team includes certified information security professionals with extensive experience in ISO 27001 implementation.",
                  "Practical Approach: We focus on creating security controls that effectively address your specific risks, not just checking boxes for compliance.",
                  "Business Integration: We ensure your ISMS aligns with and supports your business objectives and operations.",
                  "Industry Knowledge: We understand the specific security challenges facing different industries and tailor our approach accordingly.",
                  "Ongoing Support: We provide continued assistance for maintaining and improving your ISMS after certification."
                ].map((point, index) => (
                  <ListItem key={index} disablePadding sx={{ mb: 2 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={point} />
                  </ListItem>
                ))}
              </List>
            </motion.div>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Box 
                component="img"
                src="https://images.unsplash.com/photo-1531482615713-2afd69097998?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                alt="Security Consultants"
                sx={{
                  width: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                }}
              />
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* Security Statistics Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
              sx={{ mb: 2 }}
            >
              Information Security Statistics
            </Typography>
            <Typography 
              textAlign="center" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto', 
                mb: 6 
              }}
            >
              Understanding the impact of information security breaches highlights the importance of ISO 27001 certification.
            </Typography>
          </motion.div>
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(4, 1fr)' 
            },
            gap: 4 
          }}>
            {[
              {
                statistic: "83%",
                description: "Of organizations experienced phishing attacks in the past year",
                icon: <SecurityIcon fontSize="large" />
              },
              {
                statistic: "$4.35M",
                description: "Average cost of a data breach in 2022",
                icon: <MonitorHeartIcon fontSize="large" />
              },
              {
                statistic: "85%",
                description: "Of breaches involved a human element or error",
                icon: <SchoolIcon fontSize="large" />
              },
              {
                statistic: "280",
                description: "Average number of days to identify and contain a breach",
                icon: <AssessmentIcon fontSize="large" />
              }
            ].map((item, index) => (
              <Box key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      borderRadius: '16px',
                      boxShadow: '0 5px 20px rgba(0,0,0,0.05)',
                      textAlign: 'center',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 70,
                        height: 70,
                        borderRadius: '50%',
                        backgroundColor: '#1976d215',
                        color: '#1976d2',
                        mb: 3
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography
                      variant="h3"
                      component="p"
                      fontWeight="bold"
                      color="#0c2340"
                      sx={{ mb: 2 }}
                    >
                      {item.statistic}
                    </Typography>
                    <Typography color="text.secondary">
                      {item.description}
                    </Typography>
                  </Paper>
                </motion.div>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ 
        py: 12,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 6 },
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #0c2340 0%, #1e3c72 100%)',
                color: 'white',
                textAlign: 'center',
                boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                position: 'relative'
              }}
            >
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Ready to Secure Your Information Assets?
              </Typography>
              <Typography 
                variant="body1" 
                paragraph 
                sx={{ 
                  maxWidth: '700px', 
                  mx: 'auto', 
                  mb: 4, 
                  opacity: 0.9 
                }}
              >
                Our ISO 27001 experts are ready to help you implement a robust information security 
                management system and achieve certification. Contact us today for a free consultation.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  component={Link}
                  to="/contact?service=iso27001"
                  sx={{
                    bgcolor: 'white',
                    color: '#0c2340',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.9)',
                    },
                    px: 4,
                    py: 1.5,
                    borderRadius: '8px',
                    fontWeight: 'bold'
                  }}
                >
                  Contact Us
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  component={Link}
                  to="/services/iso"
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)',
                    },
                    px: 4,
                    py: 1.5,
                    borderRadius: '8px'
                  }}
                >
                  View All ISO Services
                </Button>
              </Box>

              {/* Decorative circles */}
              <Box sx={{
                position: 'absolute',
                top: -30,
                left: -30,
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.03)',
                zIndex: 0
              }} />
              <Box sx={{
                position: 'absolute',
                bottom: -40,
                right: -40,
                width: 150,
                height: 150,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.03)',
                zIndex: 0
              }} />
            </Paper>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default Iso27001; 